// app/providers.js
"use client";

import { c as _c } from "react/compiler-runtime";
import posthog from "posthog-js";
import "posthog-js/dist/exception-autocapture";
import { PostHogProvider } from "posthog-js/react";
import { useEffect } from "react";
import { beforeSendToPostHog } from "@/lib/posthog-before-send";
import { sanitizePostHogProperties } from "@/lib/posthog-privacy";
import { getTrackingConsent, useTrackingConsent } from "@/hooks/useTrackingConsent";

// PostHog keeps its identity in memory only (no cookies, no local or session
// storage) until CookieYes reports analytics consent. Events are still
// captured; each page load is then an anonymous visitor.
type PostHogPersistence = "memory" | "localStorage+cookie";
function getPostHogPersistence(hasAnalyticsConsent: boolean): PostHogPersistence {
  return hasAnalyticsConsent ? "localStorage+cookie" : "memory";
}

// Preserve early upload events while initialization yields to the first render.
const pendingEvents: Array<() => void> = [];
function sanitizePersistence(): void {
  // Flags requests bypass before_send and read SDK-cached person/initial URLs.
  // Sanitize those same stores before flags can use them, including old consented state.
  for (const persistence of Array.from(new Set([posthog.persistence, posthog.sessionPersistence]))) {
    if (!persistence) continue;
    const properties = sanitizePostHogProperties(persistence.props);
    if (properties !== persistence.props) persistence.register(properties);
  }
}
const analyticsClient = new Proxy(posthog, {
  get(target, property) {
    if (["capture", "captureException", "identify", "alias", "reset"].includes(String(property))) {
      return (...args: unknown[]) => {
        if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production" || !process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
        const deliver = () => {
          try {
            const safeArgs = args.map((value, index) => index > 0 && value !== null && typeof value === "object" && !(value instanceof Error) ? sanitizePostHogProperties(value as Record<string, unknown>) : value);
            return Reflect.apply(Reflect.get(target, property), target, safeArgs);
          } catch {
            // Analytics failures must not interrupt account or media interactions.
          }
        };
        if (target.__loaded) return deliver();
        // Account operations must survive a burst of early diagnostic events.
        const isIdentityOperation = ["identify", "alias", "reset"].includes(String(property));
        if (isIdentityOperation || pendingEvents.length < 100) pendingEvents.push(deliver);
      };
    }
    return Reflect.get(target, property);
  }
});
export function CSPostHogProvider(t0) {
  const $ = _c(8);
  const {
    children,
    bootstrapData
  } = t0;
  const {
    analytics: hasAnalyticsConsent
  } = useTrackingConsent();
  let t1;
  if ($[0] !== bootstrapData) {
    t1 = () => {
      const initialize = () => {
        if (posthog.__loaded || process.env.NEXT_PUBLIC_VERCEL_ENV !== "production" || !process.env.NEXT_PUBLIC_POSTHOG_KEY) {
          return;
        }
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
          api_host: "/ingest",
          ui_host: "https://eu.i.posthog.com",
          bootstrap: bootstrapData,
          before_send: _temp,
          autocapture: false,
          disable_session_recording: true,
          mask_all_element_attributes: true,
          mask_all_text: true,
          persistence: getPostHogPersistence(getTrackingConsent().analytics),
          capture_pageview: false,
          capture_pageleave: true,
          debug: false,
          loaded: _temp3
        });
      };
      if ("requestIdleCallback" in window) {
        const id = window.requestIdleCallback(initialize, {
          timeout: 1000
        });
        return () => window.cancelIdleCallback(id);
      }
      const id_0 = setTimeout(initialize, 0);
      return () => clearTimeout(id_0);
    };
    $[0] = bootstrapData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== hasAnalyticsConsent) {
    t3 = () => {
      if (!posthog.__loaded) {
        return;
      }
      const persistence = getPostHogPersistence(hasAnalyticsConsent);
      if (posthog.config.persistence !== persistence) {
        const previousSessionPersistence = posthog.sessionPersistence;
        if (!hasAnalyticsConsent && previousSessionPersistence !== posthog.persistence) {
          previousSessionPersistence?.clear();
        }
        posthog.set_config({
          persistence
        });
        if (!hasAnalyticsConsent) {
          const name = posthog.config.persistence_name || posthog.config.token;
          try {
            sessionStorage.removeItem(`ph_${name}_window_id`);
            sessionStorage.removeItem(`ph_${name}_primary_window_exists`);
          } catch {}
        }
      }
    };
    t4 = [hasAnalyticsConsent];
    $[3] = hasAnalyticsConsent;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== children) {
    t5 = <PostHogProvider client={analyticsClient}>{children}</PostHogProvider>;
    $[6] = children;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function _temp3(posthog) {
  sanitizePersistence();
  posthog.register({
    platform: "web",
    deployment_environment: process.env.NEXT_PUBLIC_VERCEL_ENV,
    git_commit: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    telemetry_schema_version: 2
  });
  pendingEvents.splice(0).forEach(_temp2);
  window.dispatchEvent(new Event("guestgallery:posthog-loaded"));
}
function _temp2(deliver) {
  return deliver();
}
function _temp(event) {
  sanitizePersistence();
  return beforeSendToPostHog(event);
}